import React, { useState, useEffect } from "react";
import {
    getStarPerformers,
    getStarPerformerById,
    deleteStarPerformer,
} from "../../../../services/SyncApis";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, message } from "antd";
import BackIcon from "../../../../Images/backIcon.png";
import HomeIcon from "../../../../Images/homeIcon.png";
import SearchIcon from "../../../../Images/searchIcon1.png";
import EditIcon from "../../../../Images/editIcon.png";
import DeleteIcon from "../../../../Images/deleteIcon2.png";
import PointsImage from "../../../../Images/pointsImage.png";
import RoleImage from "../../../../Images/roleImage.png";
import QuizImage from "../../../../Images/quizImage.png";
import {
    MdFirstPage,
    MdLastPage,
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
} from "react-icons/md";
import {
    Play,
    User,
    Globe,
} from "lucide-react";

const StarVideoConfiguration = () => {
    const [performers, setPerformers] = useState([]);

    const [deleteModalOpen, setDeleteModalOpen] =
        useState(false);

    const [deleteStarId, setDeleteStarId] =
        useState(null);

    const [deleteStarName, setDeleteStarName] =
        useState("");

    const [deleting, setDeleting] =
        useState(false);

    const [search, setSearch] =
        useState("");

    const [loading, setLoading] =
        useState(false);

    const [page, setPage] =
        useState(1);

    const [pageSize] =
        useState(10);

    const [pagination, setPagination] =
        useState({
            page: 1,
            pageSize: 10,
            totalRecords: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
        });

    const navigate = useNavigate();
    const location = useLocation();

    const accountId =
        location.state?.accountId;

    const roleId =
        location.state?.roleId;

    const fullState =
        location.state || {};

    const safeAccountName =
        fullState?.accountName || "";

    const safeRoleName =
        fullState?.roleName || "";

    /*
     * ============================================================
     * GET ALL STAR PERFORMERS
     * ============================================================
     */

    const fetchStarPerformers = async (
        searchTerm = "",
        currentPage = page
    ) => {
        if (!accountId || !roleId) {
            return;
        }

        setLoading(true);

        try {
            const response =
                await getStarPerformers({
                    adminClientId:
                        Number(accountId),

                    adminRoleId:
                        Number(roleId),

                    search:
                        searchTerm,

                    page:
                        currentPage,

                    pageSize:
                        pageSize,
                });

            /*
             * Backend response:
             *
             * {
             *   data: [...],
             *   pagination: {...}
             * }
             */

            const payloadArray =
                Array.isArray(response?.data)
                    ? response.data
                    : [];

            const normalized =
                payloadArray.map((raw) => {
                    /*
                     * A Star Performer can have
                     * multiple level contents.
                     *
                     * The list card represents
                     * the first configured level/video.
                     */
                    const content =
                        raw?.contents?.[0] || {};

                    const video =
                        content?.video || {};

                    const quiz =
                        content?.quizzes?.[0] || {};

                    /*
                     * Backend already maps these
                     * fields to IDs.
                     */
                    const languageId =
                        video?.languageId ??
                        raw?.defaultLanguageId ??
                        null;

                    /*
                     * Backend response contains
                     * IDs for language, so for now
                     * display the ID if no label
                     * mapping is available.
                     *
                     * This will be replaced by
                     * options API when we wire it
                     * into Add/Edit.
                     */
                    const language =
                        languageId !== null
                            ? String(languageId)
                            : "";

                    /*
                     * Thumbnail:
                     *
                     * Backend currently returns
                     * thumbnailCode, not a presigned
                     * thumbnail URL.
                     */
                    const thumbnailCode =
                        video?.thumbnailCode || "";

                    let thumbnailUrl =
                        thumbnailCode;

                    /*
                     * If thumbnailCode is already
                     * a URL, use it directly.
                     *
                     * Otherwise, if videoCode is a
                     * YouTube code, use the YouTube
                     * thumbnail URL.
                     */
                    if (
                        thumbnailCode &&
                        !/^https?:\/\//i.test(
                            thumbnailCode
                        )
                    ) {
                        const videoCode =
                            video?.videoCode;

                        if (videoCode) {
                            thumbnailUrl =
                                `https://img.youtube.com/vi/${videoCode}/0.jpg`;
                        }
                    }

                    return {
                        id:
                            raw?.id ||
                            raw?._id ||
                            null,

                        name:
                            video?.title ||
                            "",

                        type:
                            quiz?.questionTypeId
                                ? String(
                                    quiz.questionTypeId
                                )
                                : "Quiz",

                        fullName:
                            raw?.fullName ||
                            "",

                        role:
                            raw?.designation ||
                            "",

                        levelName:
                            content?.levelName ||
                            "",

                        language,

                        languageId,

                        weightage:
                            quiz?.weightage ??
                            0,

                        image:
                            thumbnailUrl,

                        isActive:
                            raw?.isActive !== false,

                        isDeleted:
                            raw?.isDeleted === true,

                        raw,
                    };
                });

            setPerformers(
                normalized.filter(
                    (item) =>
                        !item.isDeleted
                )
            );

            setPagination(
                response?.pagination || {
                    page: currentPage,
                    pageSize,
                    totalRecords:
                        normalized.length,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPreviousPage:
                        currentPage > 1,
                }
            );
        } catch (error) {
            console.error(
                "Error fetching Star Performers:",
                error
            );

            message.error(
                error?.response?.data?.message ||
                "Unable to load Star Performers."
            );

            setPerformers([]);
        } finally {
            setLoading(false);
        }
    };

    /*
     * ============================================================
     * SEARCH
     * ============================================================
     */

    useEffect(() => {
        setPage(1);

        const debounce =
            setTimeout(() => {
                fetchStarPerformers(
                    search,
                    1
                );
            }, 400);

        return () =>
            clearTimeout(debounce);
    }, [
        search,
        accountId,
        roleId,
    ]);

    /*
     * ============================================================
     * EDIT
     * ============================================================
     */

    const handleEditStar = async (
        star
    ) => {
        const idToEdit =
            star.id ||
            star.raw?.id ||
            star.raw?._id;

        if (!idToEdit) {
            message.error(
                "Unable to determine Star Performer ID."
            );
            return;
        }

        try {
            /*
             * Fetch the complete performer
             * before opening the edit page.
             *
             * The list response may contain
             * the complete contents, but GET by ID
             * is the source of truth for editing.
             */
            const response =
                await getStarPerformerById(
                    idToEdit
                );

            const starData =
                response?.data || {};

            navigate(
                "/syncAddStar",
                {
                    state: {
                        ...fullState,

                        isEdit: true,

                        accountId,

                        roleId,

                        starData,

                        starId:
                            idToEdit,
                    },
                }
            );
        } catch (error) {
            console.error(
                "Error fetching Star Performer:",
                error
            );

            message.error(
                error?.response?.data?.message ||
                "Unable to load Star Performer."
            );
        }
    };

    /*
     * ============================================================
     * DELETE
     * ============================================================
     */

    const handleDeleteStar = (
        star
    ) => {
        const idToDelete =
            star.id ||
            star.raw?.id ||
            star.raw?._id;

        const nameToDelete =
            star.fullName ||
            star.name ||
            "this Star";

        if (!idToDelete) {
            message.error(
                "Unable to determine Star Performer ID."
            );
            return;
        }

        setDeleteStarId(
            idToDelete
        );

        setDeleteStarName(
            nameToDelete
        );

        setDeleteModalOpen(
            true
        );
    };

    const confirmDeleteStar =
        async () => {
            if (!deleteStarId) {
                return;
            }

            setDeleting(true);

            const previousPerformers =
                performers;

            setPerformers(
                (prev) =>
                    prev.filter(
                        (item) =>
                            item.id !==
                            deleteStarId
                    )
            );

            try {
                /*
                 * New backend DELETE:
                 *
                 * DELETE /star-performers/{id}?changedBy={changedBy}
                 *
                 * We currently don't have
                 * changedBy wired here, so the
                 * service omits it.
                 *
                 * DO NOT send adminClientId/
                 * adminRoleId in the DELETE body.
                 */
                await deleteStarPerformer(
                    deleteStarId
                );

                message.success(
                    "Star performer deleted successfully."
                );

                fetchStarPerformers(
                    search,
                    page
                );
            } catch (error) {
                console.error(
                    "Error deleting Star Performer:",
                    error
                );

                setPerformers(
                    previousPerformers
                );

                message.error(
                    error?.response?.data?.message ||
                    "Unable to delete Star Performer."
                );
            } finally {
                setDeleting(
                    false
                );

                setDeleteModalOpen(
                    false
                );

                setDeleteStarId(
                    null
                );

                setDeleteStarName(
                    ""
                );
            }
        };

    /*
     * ============================================================
     * PAGINATION
     * ============================================================
     */

    const handleFirstPage =
        () => {
            if (page === 1) {
                return;
            }

            setPage(1);

            fetchStarPerformers(
                search,
                1
            );
        };

    const handlePreviousPage =
        () => {
            if (
                !pagination.hasPreviousPage
            ) {
                return;
            }

            const nextPage =
                page - 1;

            setPage(nextPage);

            fetchStarPerformers(
                search,
                nextPage
            );
        };

    const handleNextPage =
        () => {
            if (
                !pagination.hasNextPage
            ) {
                return;
            }

            const nextPage =
                page + 1;

            setPage(nextPage);

            fetchStarPerformers(
                search,
                nextPage
            );
        };

    const handleLastPage =
        () => {
            if (
                pagination.totalPages <= 0 ||
                page ===
                pagination.totalPages
            ) {
                return;
            }

            const nextPage =
                pagination.totalPages;

            setPage(nextPage);

            fetchStarPerformers(
                search,
                nextPage
            );
        };

    /*
     * The backend already performs
     * search, so no second client-side
     * filtering is required.
     */
    const filteredStars =
        performers;

    const handleBackToViewRole =
        () => {
            navigate(-1);
        };

    return (
        <div className="min-h-screen bg-[#F7F7F7] px-6 lg:px-12">

            {/* Header */}
            <div className="flex justify-between items-start flex-wrap gap-4 -mt-6">

                {/* Left */}
                <div className="relative top-[16px]">
                    <button
                        onClick={
                            handleBackToViewRole
                        }
                        className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                    >
                        <img
                            src={BackIcon}
                            alt="Back"
                            className="w-[15px] object-contain"
                        />
                        Back
                    </button>

                    {/* Breadcrumb */}
                    <div className="relative top-[12px] flex items-center flex-wrap text-[14px] text-[#939393] gap-1">                        <span
                        onClick={() => navigate("/admin")}
                        className="cursor-pointer"
                    >
                        {safeAccountName}
                    </span>

                        <span className="mx-1">
                            ›
                        </span>

                        <span
                            onClick={() =>
                                navigate("/experience-configuration", {
                                    state: {
                                        roleName: safeRoleName,
                                        name: safeRoleName,
                                        accountName: safeAccountName,
                                        accountId,
                                        roleId,
                                    },
                                })
                            }
                            className="cursor-pointer"
                        >
                            {safeRoleName}
                        </span>

                        <span className="mx-1">
                            ›
                        </span>

                        {/* Experience Configuration */}
                        <span
                            onClick={() =>
                                navigate("/experience-configuration", {
                                    state: {
                                        roleId,
                                        accountId,
                                        name: safeRoleName,
                                        roleName: safeRoleName,
                                        accountName: safeAccountName,
                                    },
                                })
                            }
                            className="cursor-pointer"
                        >
                            Experience Configuration
                        </span>

                        <span className="mx-1">
                            ›
                        </span>

                        <span
                            onClick={() =>
                                navigate("/productsConfiguration", {
                                    state: fullState,
                                })
                            }
                            className="cursor-pointer"
                        >
                            Product Configuration
                        </span>

                        <span className="mx-1">
                            ›
                        </span>

                        <span
                            onClick={() =>
                                navigate("/sync-configuration", {
                                    state: fullState,
                                })
                            }
                            className="cursor-pointer"
                        >
                            Sync
                        </span>

                        <span className="mx-1">
                            ›
                        </span>

                        <span className="font-semibold text-[#4A4A4A]">
                            Role Overview Videos Configuration
                        </span>

                    </div>
                </div>

                {/* Right */}
                <button
                    onClick={() =>
                        navigate(
                            "/admin"
                        )
                    }
                    className="flex items-center gap-2 mt-4 bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                >
                    <img
                        src={HomeIcon}
                        alt="Home"
                        className="w-[15px]"
                    />
                    Home
                </button>
            </div>

            {/* Main Card */}
            <div className="mt-10 bg-white rounded-xl shadow-sm p-4 flex flex-col min-h-[calc(100vh-220px)]">

                {/* Heading */}
                <h2 className="text-[17px] font-semibold text-[#1B1B1B]">
                    Role Overview Videos Configuration
                </h2>

                {/* Search + Add */}
                <div className="mt-4 flex items-center gap-3 flex-wrap">

                    <div className="flex-1 min-w-[220px] h-10 border border-[#D7D7D7] rounded-md flex items-center px-3 bg-white">

                        <input
                            type="text"
                            placeholder="Search role video makers..."
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                            className="flex-1 outline-none text-sm bg-transparent"
                        />

                        <img
                            src={SearchIcon}
                            alt="Search"
                            className="w-4 h-4 opacity-60"
                        />
                    </div>

                    <button
                        onClick={() =>
                            navigate(
                                "/syncAddStar",
                                {
                                    state: {
                                        ...fullState,
                                        isEdit: false,
                                    },
                                }
                            )
                        }
                        className="bg-[#D50000] hover:bg-[#B80000] text-white h-10 px-5 rounded-md text-sm font-medium shrink-0"
                    >
                        Add Video Maker
                    </button>
                </div>

                {/* Body — Star List */}
                <div className="flex-1 mt-4 overflow-auto">

                    {loading ? (
                        <div className="flex justify-center items-center h-full py-16">
                            <p className="text-[#A7A7A7] text-[16px]">
                                Loading...
                            </p>
                        </div>
                    ) : filteredStars.length > 0 ? (
                        filteredStars.map(
                            (star) => (
                                <div
                                    key={
                                        star.id
                                    }
                                    className="relative h-[96px] border border-[#E5E5E5] rounded-xl flex justify-between items-stretch mb-3 gap-3 flex-nowrap overflow-hidden"
                                >

                                    {star.levelName && (
                                        <span className="absolute right-4 top-3 z-10 inline-flex items-center justify-center rounded-[6px] bg-[#FFEDED] px-2.5 py-1 text-[10px] font-semibold text-[#AD0101] shadow-sm">
                                            {
                                                star.levelName
                                            }
                                        </span>
                                    )}

                                    {/* Left: thumbnail + info */}
                                    <div className="flex gap-4 items-stretch flex-1 min-w-[250px]">

                                        {/* Thumbnail */}
                                        <div className="relative w-[160px] h-full overflow-hidden shrink-0 bg-[#E8AFAF]">

                                            {star.image && (
                                                <img
                                                    src={
                                                        star.image
                                                    }
                                                    alt=""
                                                    className="w-full h-full object-cover block"
                                                />
                                            )}

                                            <div className="absolute inset-0 flex items-center justify-center">

                                                <div className="w-8 h-8 rounded-full bg-white/70 flex items-center justify-center">
                                                    <Play
                                                        size={
                                                            16
                                                        }
                                                        className="text-[#1B1B1B] ml-0.5"
                                                        fill="currentColor"
                                                    />
                                                </div>

                                            </div>
                                        </div>

                                        <div className="flex flex-col justify-center gap-1 py-3 pr-2">

                                            {/* Status */}
                                            <span
                                                className={`inline-flex items-center justify-center w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold leading-[1.2] ${star.isActive
                                                    ? "bg-[#E5F7EA] text-[#107F3F]"
                                                    : "bg-[#FBEFEF] text-[#C92A2A]"
                                                    }`}
                                            >
                                                {
                                                    star.isActive
                                                        ? "Active"
                                                        : "Inactive"
                                                }
                                            </span>

                                            {/* Title */}
                                            <h3 className="font-semibold text-[14px] text-[#1B1B1B]">
                                                {
                                                    star.name
                                                }
                                            </h3>

                                            {/* Meta row */}
                                            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#7A7A7A]">

                                                <span className="flex items-center gap-1">
                                                    <User
                                                        size={
                                                            13
                                                        }
                                                    />
                                                    {
                                                        star.fullName
                                                    }
                                                </span>

                                                <span className="flex items-center gap-2">
                                                    <img
                                                        src={
                                                            RoleImage
                                                        }
                                                        alt="Role"
                                                        className="w-4 h-4"
                                                    />
                                                    {
                                                        star.role
                                                    }
                                                </span>

                                                <span className="flex items-center gap-1">
                                                    <Globe
                                                        size={
                                                            13
                                                        }
                                                    />
                                                    {
                                                        star.language
                                                    }
                                                </span>

                                                <span className="flex items-center gap-2">
                                                    <img
                                                        src={
                                                            PointsImage
                                                        }
                                                        alt="Points"
                                                        className="w-4 h-4"
                                                    />
                                                    {
                                                        star.weightage
                                                    }{" "}
                                                    points
                                                </span>

                                                <span className="flex items-center gap-2">
                                                    <img
                                                        src={
                                                            QuizImage
                                                        }
                                                        alt="Quiz"
                                                        className="w-4 h-4"
                                                    />
                                                    {
                                                        star.type ||
                                                        "Quiz"
                                                    }
                                                </span>

                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: actions */}
                                    <div className="flex items-center gap-2 shrink-0 px-5 mt-8">

                                        <button
                                            onClick={() =>
                                                handleEditStar(
                                                    star
                                                )
                                            }
                                            type="button"
                                            aria-label="Edit video maker"
                                            className="w-7 h-7 min-w-7 min-h-7 p-0 border-0 rounded-full bg-[#F3F4F6] flex items-center justify-center cursor-pointer"
                                        >
                                            <img
                                                src={EditIcon}
                                                alt=""
                                                className="w-[15px] h-[18px] object-contain"
                                            />
                                        </button>

                                        <button
                                            type="button"
                                            aria-label="Delete video maker"
                                            onClick={() =>
                                                handleDeleteStar(
                                                    star
                                                )
                                            }
                                            className="w-7 h-7 min-w-7 min-h-7 p-0 border-0 rounded-full bg-[#F3F4F6] flex items-center justify-center cursor-pointer"
                                        >
                                            <img
                                                src={DeleteIcon}
                                                alt=""
                                                className="w-7 h-7 object-contain"
                                            />
                                        </button>

                                    </div>
                                </div>
                            )
                        )
                    ) : (
                        <div className="flex justify-center items-center h-full py-16">

                            <p className="text-[#A7A7A7] text-[18px]">
                                {
                                    search !==
                                        ""
                                        ? "No Data Found"
                                        : "No stars added yet"
                                }
                            </p>

                        </div>
                    )}
                </div>

                {/* Delete Modal */}
                <Modal
                    open={
                        deleteModalOpen
                    }
                    footer={null}
                    closable={false}
                    centered
                    width={560}
                    onCancel={() =>
                        setDeleteModalOpen(
                            false
                        )
                    }
                    destroyOnClose
                >
                    <div>

                        <h2 className="text-[20px] font-semibold text-[#1F2020] leading-7">
                            Are you sure you want to delete this Star?
                        </h2>

                        <p className="mt-4 text-[16px] leading-7 text-[#555]">
                            This will permanently delete the star performer, video, and quiz. This action cannot be undone.
                        </p>

                        <p className="mt-5 text-[16px] text-[#444]">
                            Are you{" "}
                            <span className="font-semibold">
                                Sure?
                            </span>
                        </p>

                        <div className="flex justify-end gap-4 ">

                            <button
                                onClick={() =>
                                    setDeleteModalOpen(
                                        false
                                    )
                                }
                                className="px-6 h-[45px] rounded-[10px] border border-[#D9D9D9] text-[#6B6B6B] font-medium hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={
                                    confirmDeleteStar
                                }
                                disabled={
                                    deleting
                                }
                                className="px-6 h-[45px] rounded-[10px] bg-[#C40000] text-white font-medium hover:bg-[#A50000] disabled:opacity-50"
                            >
                                {
                                    deleting
                                        ? "Deleting..."
                                        : "Delete"
                                }
                            </button>

                        </div>
                    </div>
                </Modal>

                {/* Pagination */}
                <div className="flex justify-end items-center gap-1 pt-4 border-t border-[#E5E5E5] mt-2">

                    <button
                        onClick={
                            handleFirstPage
                        }
                        disabled={
                            page === 1 ||
                            loading
                        }
                        className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-40"
                    >
                        <MdFirstPage
                            size={20}
                            className="text-[#5A5A5A]"
                        />
                    </button>

                    <button
                        onClick={
                            handlePreviousPage
                        }
                        disabled={
                            !pagination.hasPreviousPage ||
                            loading
                        }
                        className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-40"
                    >
                        <MdKeyboardArrowLeft
                            size={20}
                            className="text-[#5A5A5A]"
                        />
                    </button>

                    <span className="px-2 text-[13px] text-[#777]">
                        {page}
                        {" / "}
                        {
                            pagination.totalPages ||
                            1
                        }
                    </span>

                    <button
                        onClick={
                            handleNextPage
                        }
                        disabled={
                            !pagination.hasNextPage ||
                            loading
                        }
                        className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-40"
                    >
                        <MdKeyboardArrowRight
                            size={20}
                            className="text-[#5A5A5A]"
                        />
                    </button>

                    <button
                        onClick={
                            handleLastPage
                        }
                        disabled={
                            page ===
                            pagination.totalPages ||
                            loading
                        }
                        className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-40"
                    >
                        <MdLastPage
                            size={20}
                            className="text-[#5A5A5A]"
                        />
                    </button>

                </div>
            </div>
        </div>
    );
};

export default StarVideoConfiguration;